import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Ink from 'react-ink';
import { showToast } from './Toast';
import SignInService from '../../services/SignInService';

const LoginModal = ({ isOpen, onClose, onLoginSuccess, sourceType }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      showToast('Please enter your email address.', 'error');
      return;
    }
    if (!password) {
      showToast('Please enter your password.', 'error');
      return;
    }

    setLoading(true);
    try {
      const data = await SignInService.signIn(email, password, sourceType);
      // debugger
      if (data.auth_token) {
        const authToken = data.auth_token;

        // set auth token
        localStorage.setItem('auth_token', authToken);
        window.dispatchEvent(new Event('auth-login'));
        showToast('Login successful!', 'success');

        // Fire Rudderstack identify and login event
        try {
          if (window.rudderanalytics) {
            window.rudderanalytics.identify(data.id, {
              email: data.email,
              first_name: data.first_name,
              last_name: data.last_name,
            });
            window.rudderanalytics.track('login', {
              method: 'email',
              email: data.email,
              brand: window.gon?.current_brand || 'shipsticks',
              event_id: 'fb_event_' + data.id,
            });
          }
        } catch (e) {
          // silently ignore tracking failures
        }

        if (onLoginSuccess) {
          onLoginSuccess({ ...data, authToken });
        }

        onClose();
      } else {
        console.warn('Could not retrieve auth token from session API');
        throw new Error('Failed to retrieve user session');
      }
    } catch (error) {
      const errorMessage = 'Invalid email or password. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[480px] mx-4 relative">
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Modal Content */}
        <div className="p-8">
          <h2
            className="text-[32px] font-semibold leading-[42px] text-black mb-6"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Login
          </h2>

          <form onSubmit={handleSubmit}>
            {/* Email Field */}
            <div className="mb-5">
              <label
                className="block text-[12px] font-normal leading-[16px] text-[#252525] mb-2"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Email
              </label>
              <input
                type="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="
                  w-full h-[56px] px-4 text-[14px] font-normal leading-[18px] text-[#6E6E6E]
                  bg-white border border-gray-300 rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-[#49C44F] focus:border-transparent
                  placeholder:text-[#6E6E6E]
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
                style={{ fontFamily: 'Poppins, sans-serif' }}
              />
            </div>

            {/* Password Field */}
            <div className="mb-6">
              <label
                className="block text-[12px] font-normal leading-[16px] text-[#252525] mb-2"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Password
              </label>
              <input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                className="
                  w-full h-[56px] px-4 text-[14px] font-normal leading-[18px] text-[#6E6E6E]
                  bg-white border border-gray-300 rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-[#49C44F] focus:border-transparent
                  placeholder:text-[#6E6E6E]
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
                style={{ fontFamily: 'Poppins, sans-serif' }}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`
                w-full px-8 py-3 text-white font-semibold text-[16px] leading-[20px]
                bg-[#49C44F] rounded-lg relative
                ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#3fa841]'}
              `}
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {loading ? 'Logging in...' : 'Login'}
              {!loading && <Ink />}
            </button>
          </form>

          {/* Forgot Password Link */}
          <div className="mt-4 text-center">
            <a
              href="/users/password/new"
              className="text-[14px] font-normal text-blue-600 underline"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Forgot your password?
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

LoginModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onLoginSuccess: PropTypes.func,
  sourceType: PropTypes.string,
};

export default LoginModal;
